import { useLayoutEffect, type RefObject } from 'react'

export function revealFocusedCoachField(
  body: HTMLElement,
  input: HTMLInputElement,
  form: HTMLFormElement,
) {
  if (document.activeElement !== input) return
  const bodyBox = body.getBoundingClientRect()
  const top = bodyBox.top + body.clientTop
  const bottom = top + body.clientHeight
  const formBox = form.getBoundingClientRect()
  const target = formBox.height <= body.clientHeight ? formBox : input.getBoundingClientRect()
  const delta = target.top < top ? target.top - top : Math.max(0, target.bottom - bottom)
  // Scroll offsets can round to whole pixels; round outward to avoid a clipped final fraction.
  if (delta) body.scrollTop += delta > 0 ? Math.ceil(delta) : Math.floor(delta)
}

export function useCoachInputVisibility(
  isOpen: boolean,
  bodyRef: RefObject<HTMLDivElement | null>,
  inputRef: RefObject<HTMLInputElement | null>,
  formRef: RefObject<HTMLFormElement | null>,
) {
  useLayoutEffect(() => {
    const body = bodyRef.current
    const input = inputRef.current
    const form = formRef.current
    if (!isOpen || !body || !input || !form) return
    let frame: number | undefined
    const schedule = () => {
      if (frame !== undefined) cancelAnimationFrame(frame)
      frame = requestAnimationFrame(() => {
        frame = undefined
        // The panel's viewport fit and React layout commit run before this measurement.
        revealFocusedCoachField(body, input, form)
      })
    }
    const observer =
      typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(schedule)
    observer?.observe(body)
    observer?.observe(form)
    const viewport = window.visualViewport
    viewport?.addEventListener('resize', schedule)
    viewport?.addEventListener('scroll', schedule)
    window.addEventListener('resize', schedule)
    schedule()
    return () => {
      if (frame !== undefined) cancelAnimationFrame(frame)
      observer?.disconnect()
      viewport?.removeEventListener('resize', schedule)
      viewport?.removeEventListener('scroll', schedule)
      window.removeEventListener('resize', schedule)
    }
  }, [isOpen, bodyRef, inputRef, formRef])
}
